import { useState } from 'react'
import { useAuth } from './useAuth'
import { useEventsByIds } from './useEventsByIds'
import { useMyPregames, pregameRsvpOf } from './usePregames'
import { useUserProfiles } from './useUserProfiles'
import { todayLocalISO } from '../lib/formatDate'
import { getNow } from '../lib/clock'
import { pickHomeHeroes } from '../lib/heroEvents'
import { useUserEventInterests } from './useUserEventInterests'
import { offersTicketsAndPregames } from '@shared/pregames'
import type { Event, Pregame, UserProfile } from '../types'

const DISMISSED_KEY = 'beacon_pregame_prompt_dismissed'

function readDismissed(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? '{}') as Record<string, string>
  } catch {
    return {}
  }
}

function tomorrowLocalISO(): string {
  const d = getNow()
  d.setDate(d.getDate() + 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * The pre-game invite to ask about: one the viewer was invited to (not
 * hosting) and hasn't answered or dismissed today, happening today or
 * tomorrow — a day's notice to sort out who's going — for an event Home is
 * showing as a hero card. The earliest wins when there are several.
 */
export function usePregameRsvpPrompt(): {
  prompt: { pregame: Pregame; event: Event; host: UserProfile | undefined; tomorrow: boolean } | null
  uid: string | null
  dismiss: (pregameId: string) => void
} {
  const { signedInUid: uid } = useAuth()
  const byEvent = useMyPregames(uid)
  const [dismissed, setDismissed] = useState(readDismissed)
  const today = todayLocalISO()
  const tomorrow = tomorrowLocalISO()
  // The same hero rule Home renders from, over the user's own RSVPs.
  const { eventStatuses } = useUserEventInterests(uid)
  const { events: goingEvents } = useEventsByIds(
    [...eventStatuses].filter(([, s]) => s === 'going').map(([id]) => id),
  )
  const heroes = pickHomeHeroes(goingEvents, eventStatuses, getNow())
  const heroIds = [heroes.top?.id, heroes.inline?.id]

  const unanswered = [...byEvent.values()].flat().filter(p =>
    p.hostId !== uid && !pregameRsvpOf(p, uid) && dismissed[p.id] !== today && heroIds.includes(p.eventId))
  const { events } = useEventsByIds([...new Set(unanswered.map(p => p.eventId))])
  const byId = new Map(events.map(e => [e.id, e]))
  const match = unanswered
    .map(pregame => ({ pregame, event: byId.get(pregame.eventId) }))
    .find((m): m is { pregame: Pregame; event: Event } =>
      !!m.event && !m.event.cancelled && offersTicketsAndPregames(m.event) && [today, tomorrow].includes(m.pregame.day ?? m.event.startDate.slice(0, 10))) ?? null
  const profiles = useUserProfiles(match ? [match.pregame.hostId] : [])

  function dismiss(pregameId: string) {
    const next = { ...dismissed, [pregameId]: today }
    setDismissed(next)
    try {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(next))
    } catch {
      // Private mode: it stays dismissed for this session only.
    }
  }

  const tomorrowsPrompt = !!match && (match.pregame.day ?? match.event.startDate.slice(0, 10)) === tomorrow
  return { prompt: match && { ...match, host: profiles[match.pregame.hostId], tomorrow: tomorrowsPrompt }, uid, dismiss }
}
